import clsx from "clsx";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { TbCircleLetterC } from "react-icons/tb";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import { FiEdit } from "react-icons/fi";
import PaginationComponent from "@/app/components/ui/Pagination";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { PRIMARY_BUTTON } from "@/constants";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useCategoryListingAPI } from "@/app/hooks/api_hooks/Inventory/Category/useCategoryListingAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { Edit, EditIcon } from "lucide-react";

const PageSize = 13;

const tableHeader = [{ item: "Category Name" }, { item: "Action" }];

const CategorySetupListing = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const { NO_APPOINTMENTS } = useImageConstants();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const { execute: fetchCategory } = useCategoryListingAPI();
  const routeId = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeId);
  const { categoryList } = routeState.state as IInventoryState;
  const isProductListInProgess = useLoader("isInventoryProductListLoadingInProgess");

  useEffect(() => {
    fetchCategory(currentPage, PageSize, searchQuery);
  }, [currentPage, searchQuery]);

  return (
    <>
      <div className="flex items-center gap-3 bg-white px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex-auto min-w-0">
          <h1 className="font-semibold text-sm text-gray-900 leading-6">VMS Categories</h1>
        </div>
        <div className="flex-none">
          <button
            type="button"
            onClick={() => navigate(getRouteKey("CATEGORY_ONBOARD", "url"))}
            className={PRIMARY_BUTTON}
          >
            Add New Category
          </button>
        </div>
      </div>
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 rounded-md mt-2 shadow-sm">
        <div>
          <InputWithAddOn
            onEnterKeyPress={(query: string) => setSearchQuery(query)}
            containerClassName="w-full sm:w-1/2 lg:w-1/3"
            addon={<TbCircleLetterC className="w-4 h-4" />}
            placeholder="Search by Service Category Name"
          />
        </div>
        {isProductListInProgess ? (
          <TableSkeleton />
        ) : categoryList?.data?.length === 0 ? (
          <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Data Found." />
        ) : (
          <div className="mt-8 flow-root">
            <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
              <div className="scroll-inner !h-auto max-h-[600px]">
                <div className="w-full">
                  <div className="inline-block min-w-full align-middle">
                    <div className="bg-white rounded-lg">
                      <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                          <tr>
                            {tableHeader?.map((key, index) => (
                              <th
                                key={index}
                                scope="col"
                                className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                              >
                                {key?.item}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="overflow-hidden rounded-xl w-full">
                          {categoryList?.data?.map((cat, idx) => {
                            const isFirst = idx === 0;
                            const isLast = idx === categoryList?.data?.length - 1;
                            const isSingle = categoryList?.data?.length === 1;

                            return (
                              <tr
                                key={cat?.id}
                                className={clsx(
                                  "group bg-white transition",
                                  isSingle && "rounded-xl",
                                  isFirst && !isSingle && "rounded-t-xl",
                                  isLast && !isSingle && "rounded-b-xl",
                                )}
                              >
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                    isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                    isFirst && !isSingle && "rounded-tl-2xl",
                                    isLast && !isSingle && "rounded-bl-2xl",
                                  )}
                                >
                                  {cat?.category_name}
                                </td>
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal cursor-pointer",
                                    isSingle && "rounded-tr-2xl rounded-br-2xl",
                                    isFirst && !isSingle && "rounded-tr-2xl",
                                    isLast && !isSingle && "rounded-br-2xl",
                                  )}
                                  onClick={() => {
                                    navigate(`/app/inventory/category-onboard/${cat?.id}`);
                                  }}
                                >
                                  <FiEdit className="w-4 h-4 text-gray-500 hover:text-black" />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {categoryList?.item_count && searchQuery.trim() === "" && (
          <div className="flex justify-center items-center p-4 w-full">
            <PaginationComponent
              className="pagination-bar"
              currentPage={currentPage}
              totalCount={categoryList?.item_count}
              pageSize={PageSize}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default CategorySetupListing;
